import { useEffect, useRef, useState } from 'react';
import { Button, Card, Form, Modal, Table } from 'react-bootstrap';
import Select from 'react-select';
import { VinculoCompra, VinculoCompraOpcao, VinculoCompraOpcoes } from '../../../../@types/interfaces/safety/vinculo-compra';
import { Loading } from 'components/loading/loading-component';
import { AlertMessageType } from 'data/context/alert-context';
import { getEnv } from 'helper/window-helper';
import { UseAlert } from 'hook/alert-hook';
import { RestUseCase } from 'service/api/rest/rest-usecase';

type Props = {
  idUsuario: number;
  idEmpresa: number;
};

/** Renderiza o menu do select no body (acima da modal), para nao ser coberto pela listagem/cabecalho da tabela. */
const MENU_SOBRE_MODAL = {
  menuPortalTarget: typeof document !== 'undefined' ? document.body : undefined,
  styles: { menuPortal: (base: object) => ({ ...base, zIndex: 2000 }) },
};

const OPCOES_VAZIAS: VinculoCompraOpcoes = { filiais: [] };

type ModalProps = Props & { show: boolean; onHide: () => void };

/** Botão que abre a modal de vínculos (a modal fica fora dele, num ponto estável da árvore). */
export function VinculosCompraBotao({ onClick }: { onClick: () => void }) {
  return (
    <Button variant="outline-success" className="btn-custom me-2" size="sm" onClick={onClick}>
      Vincular Loja
    </Button>
  );
}

/** Composição completa (botão + modal) para uso onde a árvore é estável. */
export function VinculosCompraComponent({ idUsuario, idEmpresa }: Props) {
  const [aberto, setAberto] = useState(false);
  return (
    <>
      <VinculosCompraBotao onClick={() => setAberto(true)} />
      <VinculosCompraModal idUsuario={idUsuario} idEmpresa={idEmpresa} show={aberto} onHide={() => setAberto(false)} />
    </>
  );
}

export function VinculosCompraModal({ idUsuario, idEmpresa, show, onHide }: ModalProps) {
  const endpoint = getEnv('REACT_APP_END_POINT_FLUXO_APROVACAO_PEDIDO')!;
  const { showAlert } = UseAlert();
  const [loading, setLoading] = useState(false);
  const [pronto, setPronto] = useState(false);
  const [erroCarga, setErroCarga] = useState(false);
  const [vinculos, setVinculos] = useState<VinculoCompra[]>([]);
  const [opcoes, setOpcoes] = useState<VinculoCompraOpcoes>(OPCOES_VAZIAS);
  const [filial, setFilial] = useState<VinculoCompraOpcao | null>(null);
  const [salvando, setSalvando] = useState(false);
  const cargaSeq = useRef(0);

  useEffect(() => {
    if (show) {
      abrirModal();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  async function abrirModal() {
    setPronto(false);
    setErroCarga(false);
    setFilial(null);
    await carregar();
  }

  function fecharModal() {
    onHide();
    setPronto(false);
    setErroCarga(false);
  }

  async function carregar() {
    const seq = ++cargaSeq.current;
    setLoading(true);
    setErroCarga(false);
    try {
      const [respostaVinculos, respostaOpcoes] = await Promise.all([
        new RestUseCase(`${endpoint}/vinculos/usuario/${idUsuario}`).Get(),
        new RestUseCase(`${endpoint}/vinculos/opcoes?empresa=${idEmpresa}`).Get(),
      ]);
      if (seq !== cargaSeq.current) {
        return;
      }
      const opcoesCarregadas = normalizarOpcoes(respostaOpcoes.body);
      setVinculos(Array.isArray(respostaVinculos.body) ? respostaVinculos.body : []);
      setOpcoes(opcoesCarregadas);
      setPronto(opcoesProntas(opcoesCarregadas));
    } catch {
      if (seq !== cargaSeq.current) {
        return;
      }
      setVinculos([]);
      setOpcoes(OPCOES_VAZIAS);
      setPronto(false);
      setErroCarga(true);
      alertar('Não foi possível carregar os vínculos de compra.', 'danger');
    } finally {
      if (seq === cargaSeq.current) {
        setLoading(false);
      }
    }
  }

  async function adicionar() {
    if (!filial) {
      alertar('Selecione a loja.', 'warning');
      return;
    }
    if (vinculos.some((v) => v.idFilial === filial.id)) {
      alertar('Esta loja já foi vinculada.', 'warning');
      return;
    }
    setSalvando(true);
    try {
      const resposta = await new RestUseCase(`${endpoint}/vinculos/usuario/${idUsuario}`).Post({
        data: { idEmpresa, idUsuario, idFilial: filial.id },
      });
      if (!sucesso(resposta.statusCode)) {
        alertar(resposta.body?.message ?? 'Não foi possível adicionar o vínculo.', 'danger');
        return;
      }
      setVinculos((atuais) => mesclar(atuais, resposta.body ? [resposta.body] : []));
      setFilial(null);
        alertar('Vínculo adicionado.', 'success');
    } catch {
      alertar('Não foi possível adicionar o vínculo.', 'danger');
    } finally {
      setSalvando(false);
    }
  }

  async function remover(vinculo: VinculoCompra) {
    setSalvando(true);
    try {
      const resposta = await new RestUseCase(`${endpoint}/vinculos/usuario/${idUsuario}/${vinculo.id}`).Delete();
      if (!sucesso(resposta.statusCode)) {
        alertar(resposta.body?.message ?? 'Não foi possível remover o vínculo.', 'danger');
        return;
      }
      setVinculos((atuais) => atuais.filter((v) => v.id !== vinculo.id));
      alertar('Vínculo removido.', 'success');
    } catch {
      alertar('Não foi possível remover o vínculo.', 'danger');
    } finally {
      setSalvando(false);
    }
  }

  function alertar(content: string, color: AlertMessageType['color']) {
    showAlert({ show: true, content, color, time: 5000 });
  }

  const filiais = opcoes.filiais.map(toOption);
  const conteudoPronto = show && pronto && !loading;

  return (
    <>
      <Modal show={show} onHide={fecharModal} size="xl" fullscreen="md-down" backdrop="static" keyboard={false}>
        <Modal.Header closeButton>
          <Modal.Title className="ms-3 lead" style={{ fontSize: '30px' }}>
            Vínculos de compra
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ minHeight: '75vh' }}>
          {conteudoPronto ? (
            <Card>
              <Card.Header>Lojas em que o usuário pode aprovar pedidos</Card.Header>
              <Card.Body>
                <div className="row g-2 align-items-end">
                  <div className="col-md-8">
                    <Form.Label>Loja</Form.Label>
                    <Select
                      {...MENU_SOBRE_MODAL}
                      options={filiais}
                      value={filial ? toOption(filial) : null}
                      onChange={(opcao) => setFilial(opcao ? { id: opcao.value, nome: opcao.label } : null)}
                    />
                  </div>
                  <div className="col-md-2"><Button onClick={adicionar} disabled={salvando}>Adicionar</Button></div>
                </div>
                <div className="mt-3" style={{ maxHeight: 'calc(75vh - 300px)', overflowY: 'auto' }}>
                <Table striped bordered size="sm" className="mb-0">
                  <thead style={{ position: 'sticky', top: 0, background: '#fff', zIndex: 0 }}><tr><th>Loja</th><th>Ações</th></tr></thead>
                  <tbody>
                    {vinculos.map((vinculo) => (
                      <tr key={vinculo.idFilial}>
                        <td>{vinculo.nomeFilial ?? vinculo.idFilial}</td>
                        <td><Button variant="outline-danger" size="sm" onClick={() => remover(vinculo)} disabled={salvando}>Remover</Button></td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
                </div>
              </Card.Body>
            </Card>
          ) : erroCarga ? (
            <div className="text-center py-4">
              <p>Não foi possível carregar os vínculos de compra.</p>
              <Button variant="outline-primary" size="sm" onClick={carregar}>Tentar novamente</Button>
            </div>
          ) : (
            <Loading height="200px" />
          )}
        </Modal.Body>
      </Modal>
    </>
  );
}

function sucesso(statusCode: number): boolean {
  return statusCode >= 200 && statusCode < 300;
}

/** Junta os vínculos criados/atualizados pela API à lista atual, sem duplicar a loja. */
function mesclar(atuais: VinculoCompra[], novos: VinculoCompra[]): VinculoCompra[] {
  const porChave = new Map(atuais.map((v) => [v.idFilial, v]));
  novos.forEach((v) => porChave.set(v.idFilial, v));
  return Array.from(porChave.values());
}

function normalizarOpcoes(body: VinculoCompraOpcoes | null | undefined): VinculoCompraOpcoes {
  return {
    filiais: Array.isArray(body?.filiais) ? body!.filiais : [],
  };
}

function opcoesProntas(opcoes: VinculoCompraOpcoes): boolean {
  return Array.isArray(opcoes.filiais);
}

function toOption(opcao: VinculoCompraOpcao) {
  return { value: opcao.id, label: opcao.nome };
}
